import {
  AppPageHeader,
  ColumnsType,
  InfiniteTable,
} from '@/components/_common';
import { Button, Col, Modal, Row, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import ConfigurationFilterPart from './ConfigurationFilterPart';
import {
  ListStockItemsQuery,
  StockItem,
  StockItemType,
} from '@/core/domain/stock/StockItems/StockItems.types';
import { ListWorkOrdersResult } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { useDebounce } from '@/hooks';
import { useStockItems } from '@/core/domain/stock/StockItems/StockItems.hooks';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import { quantityFormatter } from '@/utils/number';
import { ListStockPartsDeliveryDataSupport } from '@/core/domain/stock/DeliveryMaterial/DeliveryMaterial.types';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';
import SpaceForButton from '@/components/_common/SpaceForButtons';

interface Props {
  open: boolean;
  onCancel: () => void;
  companyId?: number;
  workOrderSelect?: ListWorkOrdersResult;
  isConsumableNoLink?: boolean;
  consumablesSelected?: ListStockPartsDeliveryDataSupport[];
  handlerConsumablesSelect: (part: ListStockPartsDeliveryDataSupport[]) => void;
}

const ModalConsumableSelect: React.FC<Props> = ({
  open,
  onCancel,
  companyId,
  workOrderSelect,
  isConsumableNoLink,
  consumablesSelected,
  handlerConsumablesSelect,
}) => {
  const [consumables, setComsumables] = useState<
    ListStockPartsDeliveryDataSupport[]
  >([]);

  const [queryParams, setQueryParams] = useState<ListStockItemsQuery>({
    active: true,
    itemType: StockItemType.Consumable,
    companiesIds: [
      workOrderSelect?.company.id?.toString() || companyId?.toString(),
    ].filter((id): id is string => id !== undefined),
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isLoading, hasNextPage, fetchNextPage } = useStockItems(
    open,
    queryDebounce,
  );

  const consumableData = flatInfiniteResult(data);

  const columns: ColumnsType<StockItem> = [
    {
      dataIndex: 'description',
      title: 'Descrição',
      ellipsis: true,
    },
    useConfigurationColumnsPart({ isSku: true }),
    {
      dataIndex: ['subArea', 'name'],
      title: 'Área',
      ellipsis: true,
    },
    {
      dataIndex: 'costPrice',
      title: 'Preço de Custo',
      ellipsis: true,
      render: (_, row) => {
        const value = row.prices.find(
          (item) =>
            item.company.id === (workOrderSelect?.company.id || companyId),
        )?.realCost;

        return typeof value === 'number' ? quantityFormatter(value) : '-';
      },
    },
    {
      dataIndex: 'availableQuantity',
      title: 'Estoque',
      ellipsis: true,
      render: (_, row) => {
        const value = row.stock.find(
          (item) =>
            item.company.id === (workOrderSelect?.company.id || companyId),
        )?.stock;

        return typeof value === 'number' ? quantityFormatter(value) : '-';
      },
    },
  ];

  useEffect(() => {
    if (consumablesSelected) {
      setComsumables(consumablesSelected);
    }
  }, [consumablesSelected]);

  const getAvailableQuantity = (item: StockItem, companyId?: number) =>
    item.stock.find((stock) => stock.company.id === companyId)?.stock || 0;

  const getRealCost = (item: StockItem, companyId?: number) =>
    item.prices.find((price) => price.company.id === companyId)?.realCost || 0;

  const getPrices = (item: StockItem, companyId?: number) =>
    item.prices.find((price) => price.company.id === companyId)?.price || 0;

  const mapItemToWorkOrder = (
    item: StockItem,
    workOrderSelect?: ListWorkOrdersResult,
  ) => ({
    ...workOrderSelect,
    availableQuantity: getAvailableQuantity(
      item,
      isConsumableNoLink ? companyId : workOrderSelect?.company.id,
    ),
    prices: getPrices(item, workOrderSelect?.company.id || companyId),
    costPrice: getRealCost(item, workOrderSelect?.company?.id || companyId),
    requestedQuantity: 0,
    deliveredQuantity: 0,
    consumableId: item.consumableId,
    complementItemId: item.partId,
    workOrderItemId: item.consumableId,
    manufacturingCode: item.manufacturingCode,
    description: item.description,
    sku: item.sku,
    barcode: item.barcode,
    companyCode: item.companyCode?.toString(),
    subArea: item.subArea,
    workOrderNumber: isConsumableNoLink
      ? undefined
      : workOrderSelect?.complementIdentifier,
    isConsumableNoLink,
  });

  const isCheckboxDisabled = (
    item: StockItem,
    consumablesSelected?: ListStockPartsDeliveryDataSupport[],
    workOrderSelect?: ListWorkOrdersResult,
  ): boolean => {
    const isAlreadySelected = consumablesSelected?.some(
      ({ workOrderItemId }) => workOrderItemId === item.consumableId,
    );

    const targetCompanyId = isConsumableNoLink
      ? companyId
      : workOrderSelect?.company.id;

    const isOutOfStock = !item.stock.some(
      (stockItem) =>
        stockItem.company.id === targetCompanyId && stockItem.stock > 0,
    );

    return isAlreadySelected || isOutOfStock;
  };

  const addWithoutStock = () => {
    handlerConsumablesSelect(
      consumables.map((item) => ({
        ...item,
        includeOutOfStock: true,
      })),
    );
    onCancel();
  };

  const openConfirmWithoutStockModal = () => {
    Modal.confirm({
      title: 'Atenção',
      content: (
        <>
          <p>
            Ao selecionar esta opção, os itens escolhidos serão adicionados à
            listagem com quantidade zerada.
          </p>
          <p>
            Depois disso, você poderá informar manualmente a quantidade desejada
            diretamente na listagem para então concluir a entrega do material.
          </p>
        </>
      ),
      okText: 'Continuar',
      onOk: addWithoutStock,
    });
  };

  return (
    <Modal
      open={open}
      width="95%"
      destroyOnClose
      onCancel={onCancel}
      footer={
        <SpaceForButton justifyContent="end">
          <Button block onClick={onCancel}>
            Cancelar
          </Button>

          <Button block type="primary" onClick={openConfirmWithoutStockModal}>
            Incluir sem estoque
          </Button>

          <Button
            block
            type="primary"
            onClick={() => {
              handlerConsumablesSelect(consumables);
              onCancel();
            }}
          >
            Incluir
          </Button>
        </SpaceForButton>
      }
    >
      <Row gutter={[24, 24]}>
        {!isConsumableNoLink && (
          <>
            <Col xs={24} md={8} xl={8}>
              <Typography.Title level={5}>
                Orçamento: {workOrderSelect?.complementIdentifier}
              </Typography.Title>
            </Col>

            <Col xs={24} md={8} xl={8}>
              <Typography.Title level={5}>
                Orçamentista: {workOrderSelect?.estimator.name}
              </Typography.Title>
            </Col>

            <Col xs={24} md={8} xl={8}>
              <Typography.Title level={5}>
                Veículo:{' '}
                {workOrderSelect?.vehicle
                  ? workOrderSelect?.vehicle.split('(')[0]
                  : '-'}
              </Typography.Title>
            </Col>
          </>
        )}

        <AppPageHeader
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, description: value }))
          }
          extraFilters={
            <ConfigurationFilterPart
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />

        <Col span={24}>
          <InfiniteTable
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            loading={isLoading}
            columns={columns}
            dataSource={consumableData}
            rowKey={(row) => row.consumableId}
            rowSelection={{
              type: 'checkbox',
              preserveSelectedRowKeys: true,
              onChange: (_, selectedItems) => {
                setComsumables([
                  ...selectedItems.map((item) =>
                    mapItemToWorkOrder(item, workOrderSelect),
                  ),
                ]);
              },
              selectedRowKeys: consumables.map((item) => item.workOrderItemId),
              getCheckboxProps: (item) => ({
                disabled: isCheckboxDisabled(
                  item,
                  consumablesSelected,
                  workOrderSelect,
                ),
              }),
            }}
          />
        </Col>
      </Row>
    </Modal>
  );
};

export default ModalConsumableSelect;
